/**
 * The chosen theme, applied before anything is drawn.
 *
 * It used to live only inside the settings screen: `index.html` shipped with
 * `data-theme="dark"` hard-coded and the stored choice was read in an effect
 * that only ran once that screen was opened. So a light-theme user got a dark
 * app on every launch until they went to Settings and back, which is not a
 * preference — it is a screen that has to be visited to work.
 *
 * The fix is two stores rather than one. Dexie stays the record of the choice,
 * because that is what the rest of the app's settings use and what syncs. But
 * Dexie is asynchronous and a theme has to be right in the first painted frame,
 * so the same value is mirrored into `localStorage`, which the boot script in
 * `index.html` reads synchronously before React exists.
 */

import { getMeta, setMeta } from './db/dexie.js'

export type Theme = 'system' | 'light' | 'dark'

/** Read by the inline boot script too; changing it means changing both. */
export const THEME_STORAGE_KEY = 'gramas.theme'

const THEMES: readonly Theme[] = ['system', 'light', 'dark']

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value)
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  // "system" means no stamp at all: the artifact of a stamp is that it beats
  // `prefers-color-scheme`, which is exactly what following the system is not.
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)
}

/** What the boot script already applied, so React starts from the same value. */
export function storedTheme(): Theme {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY)
    return isTheme(raw) ? raw : 'dark'
  } catch {
    // Private-mode Safari throws on access rather than returning null.
    return 'dark'
  }
}

export function saveTheme(theme: Theme): void {
  applyTheme(theme)
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // A theme that cannot be remembered is still a theme that can be applied.
  }
  void setMeta('theme', theme)
}

/**
 * Reconcile the fast store with the durable one, once, at startup.
 *
 * Dexie is the older store, so an install that predates `localStorage` mirroring
 * has a choice recorded in exactly one place. Reading it here means such a user
 * gets their theme back on the second frame rather than never.
 */
export async function initTheme(): Promise<void> {
  const durable = await getMeta<Theme>('theme', storedTheme())
  if (!isTheme(durable)) return
  if (durable !== storedTheme()) saveTheme(durable)
  else applyTheme(durable)
}
